/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';

import type { Options } from '@contentful/rich-text-react-renderer';
import { BLOCKS } from '@contentful/rich-text-types';

import { Grid, Typography2 } from '@coursera/cds-core';

import type { DdpFieldsBelowFoldFragment } from 'bundles/degree-description/__generated__/contentfulTypes';
import RelatedNewsCard from 'bundles/degree-description/components/modules/related-news/RelatedNewsCard';
import Container from 'bundles/degree-description/components/shared/Container';
import {
  filterRichTextContentByNodeType,
  isEmptyChild,
  renderRichText,
} from 'bundles/degree-description/components/shared/RichText';
import { useDegreeDescriptionPageTheme } from 'bundles/degree-description/degreeDescriptionPageTheme';
import { useDegreeDescriptionEventingContext } from 'bundles/degree-description/providers/DegreeDescriptionEventingProvider';

type LatestNewsType = DdpFieldsBelowFoldFragment['latestNews'];
type LatestNewsLinksType = NonNullable<LatestNewsType>['links'];

type Props = {
  relatedNewsContent: LatestNewsType;
};

const styles = {
  wrapper: css`
    padding: var(--cds-spacing-600) 0;
  `,
};

const getRichTextRenderOptions = (links: Partial<LatestNewsLinksType>): Options => {
  return {
    renderNode: {
      [BLOCKS.HEADING_3]: (_, children) => {
        const theme = useDegreeDescriptionPageTheme();
        if (isEmptyChild(children)) return null;
        return (
          <Grid item xs={12}>
            <Typography2 variant="titleSmall" component="h2" css={theme.mobileFontSizeStyle?.header}>
              {children}
            </Typography2>
          </Grid>
        );
      },
      [BLOCKS.PARAGRAPH]: (_, children) => {
        const theme = useDegreeDescriptionPageTheme();
        if (isEmptyChild(children)) return null;
        return (
          <Grid item>
            <Typography2 variant="bodyPrimary" component="div" css={theme.mobileFontSizeStyle?.body}>
              {children}
            </Typography2>
          </Grid>
        );
      },
      [BLOCKS.EMBEDDED_ENTRY]: (node) => {
        // Only News items are rendered from here
        const id = node?.data?.target?.sys?.id;
        const entry = links?.entries?.block.find((link) => link?.sys?.id === id);
        if (entry && entry?.__typename === 'UniversityProgramNewsItem') {
          return (
            <Grid item xs={12} sm={6} md={4} lg={3}>
              <RelatedNewsCard
                key={entry.sys.id}
                title={entry.title}
                description={entry.body}
                newsDate={entry.postDate}
                timeToRead={entry.readLength}
                url={entry.url}
              />
            </Grid>
          );
        }
        return null;
      },
    },
  };
};

const RelatedNewsModule = ({ relatedNewsContent }: Props) => {
  const { useGetViewPageSectionRef } = useDegreeDescriptionEventingContext();
  const pageSectionRef = useGetViewPageSectionRef('latest_news');

  if (!relatedNewsContent) return null;
  const richTextRenderOptions = getRichTextRenderOptions(relatedNewsContent.links);
  return (
    <div css={styles.wrapper} ref={pageSectionRef}>
      <Container>
        {relatedNewsContent && (
          <Grid container spacing={24}>
            {renderRichText({
              content: filterRichTextContentByNodeType(relatedNewsContent.json, [
                BLOCKS.HEADING_3,
                BLOCKS.PARAGRAPH,
                BLOCKS.EMBEDDED_ENTRY,
              ]),
              richTextRenderOptions,
            })}
          </Grid>
        )}
      </Container>
    </div>
  );
};

export default RelatedNewsModule;
